import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { INSURANCE_QUOTATIONS_LIST_QUERY } from '../queries';
import ListBrowser, { getListBrowserStore } from '@ts-private/list-browser';
import { useApolloFetching } from '@ts-private/list-browser-apollo-extensions';
import QuotationsListActions from './QuotationsListActions';
import QuotationsContainer from './QuotationsContainer';
import QuotationsSubscriptionsArea from './QuotationsSubscriptionArea';
import { currentOfferStateCodeVar } from '../variables';
import { sortBy } from 'utils/functions';

export const insuranceQuotationsStore = getListBrowserStore({
  name: 'insurance-casco-quotations',
  withQueryFilters: false,
  orderByFields: [
    {
      caption: 'Offer.Edit.Casco.Filters.InsuranceAmount',
      value: 'insuranceAmount'
    }
  ],
  pager: {
    orderBy: 'insuranceAmount',
    direction: 'desc',
    size: 8
  },
  rowsPerPageOptions: [8, 16, 24, 32]
});

const filterInsuranceQuotations = (data, pager, filters) => {
  let quotations = [...data];

  if (filters.search)
    quotations = quotations.filter(item => {
      if (item.conventionName && item.conventionName.toLowerCase().includes(filters.search.toLowerCase())) return true;
      if (item.insurerName && item.insurerName.toLowerCase().includes(filters.search.toLowerCase())) return true;
      return false;
    });

  // #region Order
  const orderBy = [];
  if (pager.orderBy) orderBy.push(pager.orderBy);
  orderBy.push('insurerName', 'conventionName', 'period', 'quotationId');
  sortBy(quotations, orderBy, pager.direction);
  // #endregion

  // #region Additional ordering by source quotation
  let result = [];
  const withSource = quotations.filter(item => Boolean(item.sourceQuotationId)).reverse();
  for (const quotation of withSource) {
    result = [quotation, ...result];
    const sourceQuotation = quotations.find(item => item.quotationId === quotation.sourceQuotationId);
    if (sourceQuotation) result = [sourceQuotation, ...result];
  }

  const withoutSource = quotations.filter(item => !item.sourceQuotationId);
  for (const quotation of withoutSource) {
    if (!result.some(item => item.quotationId === quotation.quotationId)) result.push(quotation);
  }
  // #endregion

  return result;
};

const QuotationsListContainer = ({ documentId, insuranceTypeId, currentStateCode, contextRef, disableRecompute, recomputeQuotations }) => {
  const insuranceQuotationsFetchingOptions = useMemo(
    () => ({
      queryOptions: { query: INSURANCE_QUOTATIONS_LIST_QUERY, name: 'insuranceQuotationsList', variables: { documentId } },
      clientFilter: filterInsuranceQuotations
    }),
    [documentId]
  );

  const { setInternalValues } = useApolloFetching(insuranceQuotationsStore, insuranceQuotationsFetchingOptions);

  useEffect(() => {
    currentOfferStateCodeVar(currentStateCode);
  }, [currentStateCode]);

  return (
    <>
      <ListBrowser store={insuranceQuotationsStore} ItemsComponent={QuotationsContainer} headerOnPaper withFilterArea={false} />
      <QuotationsListActions
        documentId={documentId}
        insuranceTypeId={insuranceTypeId}
        disableRecompute={disableRecompute}
        recomputeQuotations={recomputeQuotations}
      />
      <QuotationsSubscriptionsArea contextRef={contextRef} setInternalValues={setInternalValues} />
    </>
  );
};

QuotationsListContainer.propTypes = {
  documentId: PropTypes.number,
  insuranceTypeId: PropTypes.number,
  currentStateCode: PropTypes.string,
  disableRecompute: PropTypes.bool,
  recomputeQuotations: PropTypes.func,
  contextRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.any })])
};

export default QuotationsListContainer;
